import { useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CreditCard,
  HandCoins,
  HelpCircle,
  LogOut,
  Mail,
  Phone,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Money } from '../../components/Money';
import { OptionCard } from '../../components/OptionCard';
import { useCurrentReseller } from '../../api/reseller';
import { useSession } from '../../stores/session';
import { useSubscription } from '../../api/hooks';
import { renewSubscription } from '../../api/billing';

/**
 * Suspended subscription / reactivation — §7.6 / §8.2
 * Full-screen page; reseller-branded support contact.
 */
export function Suspended() {
  const [pay, setPay] = useState<'sslcommerz' | 'cash'>('sslcommerz');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: currentReseller } = useCurrentReseller();
  const businessId = useSession((s) => s.activeBusinessId);
  const email = useSession((s) => s.user?.email);
  const { data: sub } = useSubscription(businessId ?? '');
  if (!currentReseller) return null;
  const accent = currentReseller.primaryColor;

  async function payAndRestore() {
    if (pay === 'cash') return; // cash is recorded by staff — see the support contacts below
    if (!businessId) {
      setError('No active business in this session. Please sign in again.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { gatewayPageURL } = await renewSubscription(businessId);
      window.location.href = gatewayPageURL; // hosted SSLCommerz checkout
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark px-4 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto">
        {/* In-app warning copy banner */}
        <Banner
          tone="warning"
          title="Your subscription payment is overdue."
          className="mb-6"
        >
          Pay now to restore access and avoid a longer suspension. Your data is safe.
        </Banner>

        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <span
            className="w-12 h-12 rounded-xl grid place-items-center text-white font-semibold text-lg shadow-sm"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {currentReseller.displayName.charAt(0)}
          </span>
          <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            {currentReseller.displayName}
          </p>
        </div>

        {/* Main card */}
        <Card className="p-6 sm:p-8">
          <div className="flex items-start gap-4 pb-5 mb-5 border-b border-zinc-200 dark:border-zinc-700">
            <span className="w-10 h-10 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex-shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </span>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                Your subscription is on hold
              </h1>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                Restore access in under a minute. Your data is safe.
              </p>
            </div>
          </div>

          <dl className="grid sm:grid-cols-2 gap-4 mb-6">
            <Stat label="Plan" value={sub ? `${sub.plan} · ${sub.interval === 'year' ? 'yearly' : 'monthly'}` : '—'} />
            <Stat label="Status" value={sub ? sub.status.replace(/_/g, ' ') : '—'} />
            <Stat label="Next due" value={sub?.renewsAt ?? '—'} />
            <Stat
              label="Amount due"
              value={
                sub ? (
                  <Money
                    minor={sub.amount}
                    hideCents
                    tone="neutral"
                    className="font-semibold text-rose-600 dark:text-rose-400 text-base"
                  />
                ) : (
                  '—'
                )
              }
            />
          </dl>

          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">
              Choose a payment method
            </span>
            <div className="space-y-3">
              <OptionCard
                selected={pay === 'sslcommerz'}
                onSelect={() => setPay('sslcommerz')}
              >
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                  <div>
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">
                      Pay now via SSLCommerz
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                      Card / bKash / Nagad / bank — instant restore.
                    </p>
                  </div>
                </div>
              </OptionCard>
              <OptionCard selected={pay === 'cash'} onSelect={() => setPay('cash')}>
                <div className="flex items-start gap-3">
                  <HandCoins className="w-5 h-5 mt-0.5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">
                      Pay cash at office
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                      We'll share office / bank details · {currentReseller.supportPhone}
                    </p>
                  </div>
                </div>
              </OptionCard>
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-rose-600 dark:text-rose-400" role="alert">
              {error}
            </p>
          )}
          <Button
            fullWidth
            size="lg"
            className="mt-6"
            onClick={payAndRestore}
            loading={submitting}
            disabled={submitting || pay === 'cash'}
            iconRight={!submitting && pay !== 'cash' ? <ArrowRight className="w-4 h-4" /> : undefined}
          >
            {pay === 'cash' ? 'Contact support to pay cash' : 'Pay & restore'}
          </Button>
          {pay === 'cash' && (
            <p className="mt-2 text-center text-xs text-zinc-500 dark:text-zinc-400">
              Cash is recorded by our team — call or email using the details below.
            </p>
          )}
        </Card>

        {/* Support */}
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <Card className="p-4 flex items-center gap-3">
            <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
              <Mail className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Email support</div>
              <a
                href={`mailto:${currentReseller.supportEmail}`}
                className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:underline truncate mono"
              >
                {currentReseller.supportEmail}
              </a>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-3">
            <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
              <Phone className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Call us</div>
              <a
                href={`tel:${currentReseller.supportPhone}`}
                className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:underline mono"
              >
                {currentReseller.supportPhone}
              </a>
            </div>
          </Card>
        </div>

        <div className="mt-8 flex items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
          <span>
            Logged in as{' '}
            <span className="mono text-zinc-700 dark:text-zinc-200">{email ?? '—'}</span>
          </span>
          <div className="flex items-center gap-3">
            <a
              href={`mailto:${currentReseller.supportEmail}`}
              className="inline-flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-200"
            >
              <HelpCircle className="w-3.5 h-3.5" /> Help
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 px-4 py-3">
      <dt className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100 tabular-nums">
        {value}
      </dd>
    </div>
  );
}
